import { create } from 'zustand';
import {
  api,
  subscribe,
  realtime,
  signIn as authSignIn,
  signOut as authSignOut,
  getSession,
  normalizeScreenName,
  sameScreenName,
  effectivePresence,
  PRESENCE_HEARTBEAT_MS,
  IDLE_AFTER_MS,
  type Buddy,
  type Message,
  type Presence,
  type TypingState,
  type User,
} from '@ogaim/core';

export type Screen = 'signon' | 'signup' | 'buddylist';

/** Conversations are keyed by normalized screen name throughout. */
export interface Conversation {
  /** Canonical casing, for display. */
  screenName: string;
  messages: Message[];
  /** Set when they are typing to us; cleared on message or timeout. */
  theirTyping: TypingState;
  unread: number;
  loading: boolean;
  loaded: boolean;
  nextToken: string | null;
}

interface State {
  screen: Screen;
  me: User | null;
  signingOn: boolean;
  /** 0..1, drives the sign-on progress bar. */
  signOnProgress: number;
  error: string | null;

  buddies: Buddy[];
  buddiesLoading: boolean;

  conversations: Record<string, Conversation>;
  /** Normalized screen names of open IM windows, in the order opened. */
  openWindows: string[];
  activeWindow: string | null;

  connection: 'closed' | 'connecting' | 'ready';

  signIn(screenName: string, password: string): Promise<void>;
  signOff(): void;
  restoreSession(): Promise<void>;
  goTo(screen: Screen): void;
  clearError(): void;

  refreshBuddies(): Promise<void>;
  addBuddy(screenName: string, group?: string): Promise<void>;
  removeBuddy(screenName: string): Promise<void>;

  openConversation(screenName: string): void;
  closeConversation(screenName: string): void;
  focusConversation(screenName: string): void;
  loadHistory(screenName: string): Promise<void>;
  send(screenName: string, body: string): Promise<void>;
  notifyTyping(screenName: string, state: TypingState): void;

  setPresence(presence: Presence, awayMessage?: string | null): Promise<void>;
  noteActivity(): void;
}

const emptyConversation = (screenName: string): Conversation => ({
  screenName,
  messages: [],
  theirTyping: 'NONE',
  unread: 0,
  loading: false,
  loaded: false,
  nextToken: null,
});

/** Handles returned by subscribe.*; torn down on sign off. */
let teardown: Array<() => void> = [];
let heartbeat: ReturnType<typeof setInterval> | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let lastActivity = Date.now();
/** Debounce timers for our own outbound typing notifications, per buddy. */
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useStore = create<State>((set, get) => ({
  screen: 'signon',
  me: null,
  signingOn: false,
  signOnProgress: 0,
  error: null,
  buddies: [],
  buddiesLoading: false,
  conversations: {},
  openWindows: [],
  activeWindow: null,
  connection: 'closed',

  goTo: (screen) => set({ screen, error: null }),
  clearError: () => set({ error: null }),

  async signIn(screenName, password) {
    set({ signingOn: true, error: null, signOnProgress: 0.15 });
    try {
      await authSignIn(screenName.trim(), password);
      set({ signOnProgress: 0.45 });
      await bootstrap(set, get);
    } catch (err: any) {
      set({ signingOn: false, signOnProgress: 0, error: err?.message ?? 'Sign on failed.' });
      throw err;
    }
  },

  async restoreSession() {
    const session = await getSession();
    if (!session) return;
    set({ signingOn: true, signOnProgress: 0.45 });
    try {
      await bootstrap(set, get);
    } catch {
      // A stale session is not an error worth showing; just stay on sign-on.
      set({ signingOn: false, signOnProgress: 0 });
    }
  },

  signOff() {
    // Best effort: tell the service we are gone, but do not block sign-off on it.
    void api.setPresence('OFFLINE').catch(() => {});
    for (const fn of teardown) fn();
    teardown = [];
    if (heartbeat) clearInterval(heartbeat);
    if (idleTimer) clearTimeout(idleTimer);
    heartbeat = null;
    idleTimer = null;
    for (const t of typingTimers.values()) clearTimeout(t);
    typingTimers.clear();
    realtime.close();
    authSignOut();
    set({
      screen: 'signon',
      me: null,
      buddies: [],
      conversations: {},
      openWindows: [],
      activeWindow: null,
      signingOn: false,
      signOnProgress: 0,
      connection: 'closed',
    });
  },

  async refreshBuddies() {
    set({ buddiesLoading: true });
    try {
      const buddies = await api.buddyList();
      set({ buddies });
      watchBuddyPresence(set, get);
    } finally {
      set({ buddiesLoading: false });
    }
  },

  async addBuddy(screenName, group) {
    const buddy = await api.addBuddy(screenName.trim(), group);
    set((s) => ({
      buddies: [...s.buddies.filter((b) => !sameScreenName(b.screenName, buddy.screenName)), buddy],
    }));
    watchBuddyPresence(set, get);
  },

  async removeBuddy(screenName) {
    await api.removeBuddy(screenName);
    set((s) => ({ buddies: s.buddies.filter((b) => !sameScreenName(b.screenName, screenName)) }));
  },

  openConversation(screenName) {
    const key = normalizeScreenName(screenName);
    set((s) => ({
      conversations: s.conversations[key]
        ? { ...s.conversations, [key]: { ...s.conversations[key]!, unread: 0 } }
        : { ...s.conversations, [key]: emptyConversation(screenName) },
      openWindows: s.openWindows.includes(key) ? s.openWindows : [...s.openWindows, key],
      activeWindow: key,
    }));
    void get().loadHistory(screenName);
  },

  closeConversation(screenName) {
    const key = normalizeScreenName(screenName);
    set((s) => {
      const openWindows = s.openWindows.filter((k) => k !== key);
      return {
        openWindows,
        activeWindow:
          s.activeWindow === key ? openWindows[openWindows.length - 1] ?? null : s.activeWindow,
      };
    });
  },

  focusConversation(screenName) {
    const key = normalizeScreenName(screenName);
    set((s) => ({
      activeWindow: key,
      conversations: s.conversations[key]
        ? { ...s.conversations, [key]: { ...s.conversations[key]!, unread: 0 } }
        : s.conversations,
    }));
  },

  async loadHistory(screenName) {
    const key = normalizeScreenName(screenName);
    const existing = get().conversations[key];
    if (existing?.loaded || existing?.loading) return;

    patchConvo(set, key, screenName, (c) => ({ ...c, loading: true }));
    try {
      const page = await api.conversation(screenName, 50);
      patchConvo(set, key, screenName, (c) => ({
        ...c,
        // The API returns newest-first; the transcript reads oldest-first.
        messages: mergeMessages([...page.items].reverse(), c.messages),
        nextToken: page.nextToken,
        loading: false,
        loaded: true,
      }));
    } catch {
      patchConvo(set, key, screenName, (c) => ({ ...c, loading: false }));
    }
  },

  async send(screenName, body) {
    const key = normalizeScreenName(screenName);
    const sent = await api.sendMessage(screenName, body);
    patchConvo(set, key, screenName, (c) => ({
      ...c,
      messages: mergeMessages(c.messages, [sent]),
    }));
    // Sending implicitly ends the typing state.
    get().notifyTyping(screenName, 'NONE');
    get().noteActivity();
  },

  notifyTyping(screenName, state) {
    const key = normalizeScreenName(screenName);
    const pending = typingTimers.get(key);
    if (pending) clearTimeout(pending);
    // Coalesce keystrokes: one mutation per 1.5s per conversation, not per key.
    const delay = state === 'TYPING' ? 1500 : 0;
    typingTimers.set(
      key,
      setTimeout(() => {
        typingTimers.delete(key);
        void api.setTyping(screenName, state).catch(() => {});
      }, delay)
    );
  },

  async setPresence(presence, awayMessage) {
    const ev = await api.setPresence(presence, awayMessage);
    set((s) => ({
      me: s.me ? { ...s.me, presence: ev.presence, awayMessage: ev.awayMessage, lastSeen: ev.lastSeen } : s.me,
    }));
  },

  noteActivity() {
    lastActivity = Date.now();
    const me = get().me;
    // Coming back from idle restores ONLINE, but never overrides an explicit
    // Away — the user chose that and only they should clear it.
    if (me?.presence === 'IDLE') void get().setPresence('ONLINE');
    scheduleIdleCheck(get);
  },
}));

/* ------------------------------------------------------------------ helpers */

type Setter = (fn: (s: State) => Partial<State>) => void;

/** Signs on: loads the profile, opens subscriptions, starts the heartbeat. */
async function bootstrap(set: Setter | any, get: () => State) {
  const me = await api.me();
  set({ me, signOnProgress: 0.7 });

  realtime.onStateChange((connection) => set({ connection }));

  teardown.push(
    subscribe.messages(me.screenName, {
      next: (msg) => {
        const key = normalizeScreenName(msg.from);
        const state = get();
        const isOpen = state.activeWindow === key;
        const buddy = state.buddies.find((b) => sameScreenName(b.screenName, msg.from));
        const display = buddy?.screenName ?? msg.from;
        if (!buddy) {
          // Not on the buddy list, so their capitalisation is unknown. Fetch it
          // in the background; the window opens immediately with the id.
          void api
            .getUser(msg.from)
            .then((u) => {
              if (u) patchConvo(set, key, u.screenName, (c) => ({ ...c, screenName: u.screenName }));
            })
            .catch(() => {});
        }
        patchConvo(set, key, display, (c) => ({
          ...c,
          messages: mergeMessages(c.messages, [msg]),
          theirTyping: 'NONE',
          unread: isOpen ? 0 : c.unread + 1,
        }));
        // An incoming IM opens a window, exactly like the original did.
        set((s: State) => ({
          openWindows: s.openWindows.includes(key) ? s.openWindows : [...s.openWindows, key],
          activeWindow: s.activeWindow ?? key,
        }));
      },
    })
  );

  teardown.push(
    subscribe.typing(me.screenName, {
      next: (ev) => {
        const key = normalizeScreenName(ev.from);
        patchConvo(set, key, ev.from, (c) => ({ ...c, theirTyping: ev.state }));
      },
    })
  );

  set({ signOnProgress: 0.85 });
  await get().refreshBuddies();

  await api.setPresence('ONLINE');
  set({ me: { ...me, presence: 'ONLINE' } });

  if (heartbeat) clearInterval(heartbeat);
  heartbeat = setInterval(() => {
    const current = get().me;
    if (!current) return;
    // Re-asserting presence doubles as the liveness heartbeat that keeps
    // buddies from seeing us go stale.
    void api.setPresence(current.presence, current.awayMessage).catch(() => {});
  }, PRESENCE_HEARTBEAT_MS);

  lastActivity = Date.now();
  scheduleIdleCheck(get);

  set({ screen: 'buddylist', signingOn: false, signOnProgress: 1 });
}

function scheduleIdleCheck(get: () => State) {
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    const me = get().me;
    if (!me) return;
    if (me.presence === 'ONLINE' && Date.now() - lastActivity >= IDLE_AFTER_MS) {
      void get().setPresence('IDLE');
    }
  }, IDLE_AFTER_MS + 1000);
}

/** One presence subscription per buddy; re-synced whenever the list changes. */
let presenceWatchers = new Map<string, () => void>();
function watchBuddyPresence(set: any, get: () => State) {
  const wanted = new Set(get().buddies.map((b) => normalizeScreenName(b.screenName)));

  for (const [key, unsub] of presenceWatchers) {
    if (!wanted.has(key)) {
      unsub();
      presenceWatchers.delete(key);
    }
  }

  for (const buddy of get().buddies) {
    const key = normalizeScreenName(buddy.screenName);
    if (presenceWatchers.has(key)) continue;
    const unsub = subscribe.presence(buddy.screenName, {
      next: (ev) => {
        set((s: State) => ({
          buddies: s.buddies.map((b) =>
            sameScreenName(b.screenName, ev.screenName)
              ? { ...b, presence: ev.presence, awayMessage: ev.awayMessage, lastSeen: ev.lastSeen }
              : b
          ),
        }));
      },
    });
    presenceWatchers.set(key, unsub);
    teardown.push(unsub);
  }
}

function patchConvo(
  set: any,
  key: string,
  screenName: string,
  fn: (c: Conversation) => Conversation
) {
  set((s: State) => ({
    conversations: {
      ...s.conversations,
      [key]: fn(s.conversations[key] ?? emptyConversation(screenName)),
    },
  }));
}

/**
 * Merges by id and sorts by sentAt. A message can arrive twice — once from the
 * mutation response and once over the subscription — so dedupe is required.
 */
function mergeMessages(a: Message[], b: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const m of a) byId.set(m.id, m);
  for (const m of b) byId.set(m.id, m);
  return [...byId.values()].sort((x, y) => x.sentAt - y.sentAt);
}

export interface BuddyGroup {
  name: string;
  buddies: Array<Buddy & { effective: Presence }>;
  online: number;
}

/**
 * Groups buddies for display, offline sunk to the bottom.
 *
 * Deliberately a plain function over `buddies` rather than a zustand selector.
 * It builds fresh arrays every call, and zustand compares selector results with
 * Object.is — as a selector it would report a change on every store read and
 * spin the component in an infinite render loop. Callers select the stable
 * `buddies` array and wrap this in useMemo.
 */
export function groupBuddies(buddies: Buddy[], now = Date.now()): BuddyGroup[] {
  const groups = new Map<string, Array<Buddy & { effective: Presence }>>();
  for (const b of buddies) {
    const effective = effectivePresence(b.presence, b.lastSeen, now);
    const list = groups.get(b.group) ?? [];
    list.push({ ...b, effective });
    groups.set(b.group, list);
  }
  return [...groups.entries()]
    .map(([name, list]) => ({
      name,
      buddies: list.sort((x, y) => {
        const rank = (p: Presence) => (p === 'OFFLINE' ? 1 : 0);
        return rank(x.effective) - rank(y.effective) ||
          x.screenName.localeCompare(y.screenName);
      }),
      online: list.filter((b) => b.effective !== 'OFFLINE').length,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}
